import React, { useState, useEffect, useContext, useCallback } from "react";
import "./EditReportSketchDesign.css";
import {
  getSketchDesignReportsByUser,
  getGeneralDesignReportsByUser,
  updateSketchDesignReport,
  updateGeneralDesignReport,
  deleteSketchDesignReport,
  deleteGeneralDesignReport,
} from "../../../api/reportsApi";
import { getSketchDesignProjects } from "../../../api/projectApi";
import { getSketchDesignEstimateWorkTypes } from "../../../api/projectEstimatesApi";
import { UserContext } from "../../../contexts/CurrentUserContext";
import Preloader from "../../Preloader/Preloader";
import EditReportSketchDesignModal from "./EditReportSketchDesignModal";
import EditReportGeneralDesignModal from "./EditReportGeneralDesignModal";

const REPORT_KIND = {
  SKETCH: "sketch",
  GENERAL: "general_design",
};

const formatSecondsSpend = (secondsSpend) => {
  if (secondsSpend == null) return "-";
  const totalMinutes = Math.floor(Number(secondsSpend) / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const EditReportSketchDesign = () => {
  const { userData } = useContext(UserContext);
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [generalDesignProjects, setGeneralDesignProjects] = useState([]);
  const [workTypesByProject, setWorkTypesByProject] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupError, setPopupError] = useState(null);

  const userId = userData?.id;

  const loadWorkTypesForSketchReports = useCallback(async (list) => {
    const projectIds = [
      ...new Set(list.map((r) => r.project_id).filter(Boolean)),
    ];
    const wtMap = {};
    await Promise.all(
      projectIds.map(async (pid) => {
        try {
          const raw = await getSketchDesignEstimateWorkTypes(pid);
          const wts = Array.isArray(raw) ? raw : raw?.work_types ?? [];
          wtMap[pid] = Array.isArray(wts) ? wts : [];
        } catch {
          wtMap[pid] = [];
        }
      })
    );
    return wtMap;
  }, []);

  const loadReports = useCallback(async () => {
    const [sketchReportsData, generalReportsData, projectsData] =
      await Promise.all([
        getSketchDesignReportsByUser(userId),
        getGeneralDesignReportsByUser(userId),
        getSketchDesignProjects(),
      ]);

    const sketchList = Array.isArray(sketchReportsData) ? sketchReportsData : [];
    const generalList = Array.isArray(generalReportsData)
      ? generalReportsData
      : [];
    const allProjects = Array.isArray(projectsData) ? projectsData : [];

    const tagged = [
      ...sketchList.map((r) => ({ ...r, reportKind: REPORT_KIND.SKETCH })),
      ...generalList.map((r) => ({
        ...r,
        reportKind: REPORT_KIND.GENERAL,
      })),
    ].sort(
      (a, b) => new Date(b.datetime || 0) - new Date(a.datetime || 0)
    );

    const wtMap = await loadWorkTypesForSketchReports(sketchList);

    return {
      reports: tagged,
      projects: allProjects.filter((p) => p.source !== "general_design"),
      generalDesignProjects: allProjects.filter(
        (p) => p.source === "general_design"
      ),
      workTypesByProject: wtMap,
    };
  }, [userId, loadWorkTypesForSketchReports]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await loadReports();
        if (cancelled) return;
        setReports(data.reports);
        setProjects(data.projects);
        setGeneralDesignProjects(data.generalDesignProjects);
        setWorkTypesByProject(data.workTypesByProject);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Ошибка загрузки отчётов");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [userId, loadReports]);

  const refreshReports = async () => {
    const data = await loadReports();
    setReports(data.reports);
    setProjects(data.projects);
    setGeneralDesignProjects(data.generalDesignProjects);
    setWorkTypesByProject(data.workTypesByProject);
  };

  const getProjectName = (projectId) => {
    const p = projects.find((x) => x.id === projectId);
    return p?.name ?? projectId ?? "-";
  };

  const getWorkTypeName = (projectId, workTypeId) => {
    const wts = workTypesByProject[projectId] || [];
    const wt = wts.find((x) => x.id === workTypeId);
    return wt?.name ?? workTypeId ?? "-";
  };

  const getGeneralDesignLabel = (contractSectionsId) => {
    const p = generalDesignProjects.find(
      (x) => x.contract_sections_id === contractSectionsId
    );
    if (!p) return contractSectionsId ?? "-";
    return p.section_name ? `${p.name} — ${p.section_name}` : p.name;
  };

  const getReportTypeLabel = (report) =>
    report.reportKind === REPORT_KIND.GENERAL ? "Генплан РП" : "ЭП";

  const handleReportClick = (report) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReport(null);
    setPopupError(null);
  };

  const showSuccess = (message) => {
    setPopupMessage(message);
    setPopupError(null);
    setShowSuccessPopup(true);
    setTimeout(() => setShowSuccessPopup(false), 3000);
  };

  const handleSaveSketchReport = async (payload) => {
    if (!selectedReport?.id) return;
    try {
      await updateSketchDesignReport(selectedReport.id, payload);
      await refreshReports();
      handleCloseModal();
      showSuccess("Отчёт ЭП успешно обновлён");
    } catch (err) {
      setPopupError(err?.message || "Ошибка сохранения");
      throw err;
    }
  };

  const handleSaveGeneralReport = async (payload) => {
    if (!selectedReport?.id) return;
    try {
      await updateGeneralDesignReport(payload);
      await refreshReports();
      handleCloseModal();
      showSuccess("Отчёт Генплан РП успешно обновлён");
    } catch (err) {
      setPopupError(err?.message || "Ошибка сохранения");
      throw err;
    }
  };

  const handleDeleteSketchReport = async () => {
    if (!selectedReport?.id) return;
    try {
      await deleteSketchDesignReport(selectedReport.id);
      await refreshReports();
      handleCloseModal();
      showSuccess("Отчёт ЭП удалён");
    } catch (err) {
      setPopupError(err?.message || "Ошибка удаления");
      throw err;
    }
  };

  const handleDeleteGeneralReport = async () => {
    if (!selectedReport?.id) return;
    try {
      await deleteGeneralDesignReport(selectedReport.id);
      await refreshReports();
      handleCloseModal();
      showSuccess("Отчёт Генплан РП удалён");
    } catch (err) {
      setPopupError(err?.message || "Ошибка удаления");
      throw err;
    }
  };

  if (loading && reports.length === 0) {
    return <Preloader />;
  }

  if (error) {
    return (
      <div className="edit-report-sketch error-message">
        Ошибка при загрузке отчётов: {error}
      </div>
    );
  }

  return (
    <div className="edit-report-sketch">
      <h2 className="edit-report-sketch__title">
        Редактирование отчётов (эскизное отделение)
      </h2>
      {reports.length === 0 ? (
        <p className="edit-report-sketch__empty">
          Нет отчётов
        </p>
      ) : (
        <table className="edit-report-sketch-table">
          <thead>
            <tr>
              <th>Дата</th>
              <th>Тип</th>
              <th>Проект / раздел</th>
              <th>Часы</th>
              <th>Вид работ</th>
              <th>Комментарий</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report) => (
              <tr
                key={`${report.reportKind}-${report.id}`}
                onClick={() => handleReportClick(report)}
                className={selectedReport?.id === report.id ? "selected" : ""}
              >
                <td>
                  {report.datetime
                    ? new Date(report.datetime).toLocaleDateString("ru-RU", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      })
                    : "-"}
                </td>
                <td>{getReportTypeLabel(report)}</td>
                <td>
                  {report.reportKind === REPORT_KIND.GENERAL
                    ? getGeneralDesignLabel(report.contract_sections_id)
                    : getProjectName(report.project_id)}
                </td>
                <td>{formatSecondsSpend(report.seconds_spend)}</td>
                <td>
                  {report.reportKind === REPORT_KIND.GENERAL
                    ? "—"
                    : getWorkTypeName(report.project_id, report.work_type_id)}
                </td>
                <td>{report.comment || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isModalOpen && selectedReport?.reportKind === REPORT_KIND.SKETCH && (
        <EditReportSketchDesignModal
          report={selectedReport}
          projects={projects}
          workTypesByProject={workTypesByProject}
          onClose={handleCloseModal}
          onSave={handleSaveSketchReport}
          onDelete={handleDeleteSketchReport}
          error={popupError}
        />
      )}

      {isModalOpen && selectedReport?.reportKind === REPORT_KIND.GENERAL && (
        <EditReportGeneralDesignModal
          report={selectedReport}
          generalDesignProjects={generalDesignProjects}
          onClose={handleCloseModal}
          onSave={handleSaveGeneralReport}
          onDelete={handleDeleteGeneralReport}
          error={popupError}
        />
      )}

      {showSuccessPopup && (
        <div className="popup-overlay">
          <div className={`popup ${popupError ? "popup-error" : ""}`}>
            <div
              className={`popup-icon ${popupError ? "popup-icon-error" : ""}`}
            />
            <p className="popup-text">
              {popupError || popupMessage || "Отчёт успешно обновлён"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditReportSketchDesign;
